<script context="module" lang="ts">
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import { gql } from 'graphql-request';

	import Section from '$lib/components/layout/Section.svelte';
	import Loader from '$lib/components/util/Loader.svelte';
	import Text from '$lib/components/Text.svelte';
	import Media from '$lib/components/media/Media.svelte';

	export const config: SectionConfig = {
		collection: 'section_people',
		availableFor: ['pages', 'special-pages'],
		query: gql`
      translations {
        languages_code {
          code
        }
        title
        subtitle
      }
      people {
        team_id {
          image {
            id
            type
            translations {
              languages_code {
                code
              }
              media_description
            }
          }
          name
          phone
          email
          translations {
            languages_code {
              code
            }
            position
            text
          }
        }
      }
    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		subtitle: string;
		people: Array<any>;
	};
</script>

<script lang="ts">
	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
</script>

<Section>
	<div class="header">
		<h2>{data.title}</h2>
		<p>{data.subtitle}</p>
	</div>
	<div class="grid">
		{#each data.people as person, i}
			<div class="person animated fade-up started" style="animation-delay: {100 * i}ms">
				<div class="image">
					<Media {...person.team_id?.image} transform="s" fit="cover" />
				</div>

				<div class="name">{person.team_id?.name}</div>
				{#if person.team_id?.position}
					<div class="position">{person.team_id?.position}</div>
				{/if}
				{#if person.team_id?.phone}
					<div class="phone">
						<a href={'tel:' + person.team_id?.phone}>{person.team_id?.phone}</a>
					</div>
				{/if}
				{#if person.team_id?.email}
					<div class="email">
						<a href={'mailto:' + person.team_id?.email}>{person.team_id?.email}</a>
					</div>
				{/if}
				{#if person.team_id?.text}
					<div class="text">{person.team_id?.text}</div>
				{/if}
			</div>
		{/each}
	</div>
</Section>

<style lang="scss">
	.header {
		margin-bottom: v(content-padding-lg);

		h2 {
			@include font(h2);
		}

		.subheadline {
			@include font(text-lg);
		}
	}

	.grid {
		width: 100%;
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));

		@include media(tablet) {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}

		@media (max-width: 660px) {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		@media (max-width: 500px) {
			grid-template-columns: repeat(1, minmax(0, 1fr));
		}

		grid-gap: v(content-padding);

		.person {
			display: flex;
			flex-direction: column;
			align-items: center;
			box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.1);
			padding: 16px;

			.image {
				width: 90%;
				aspect-ratio: 1;
				border-radius: 100%;
				overflow: hidden;
				margin-bottom: v(content-padding);
			}

			.name,
			.position,
			.phone,
			.email,
			.text {
				width: 100%;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.position,
			.email {
				margin-bottom: v(content-padding);
			}
		}
	}
</style>
